import { Link, usePage } from '@inertiajs/react';
import { m, AnimatePresence } from 'framer-motion';
import {
    ClipboardCheck,
    Home,
    LayoutGrid,
    MoreHorizontal,
    Users,
    LogOut,
} from 'lucide-react';
import { useState } from 'react';
import { mainNavItems } from '@/components/app-sidebar';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export function AdminBottomNav() {
    const { url } = usePage();
    const [isOpen, setIsOpen] = useState(false);

    const primaryTabs = [
        { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutGrid, key: 'dashboard' },
        { name: 'Santri', href: '/admin/santri', icon: Users, key: 'santri' },
        { name: 'Izin', href: '/admin/izin', icon: ClipboardCheck, key: 'izin' },
        { name: 'Pulang', href: '/admin/kepulangan', icon: Home, key: 'kepulangan' },
    ];

    function isActive(href: string): boolean {
        if (href === '/admin/dashboard') return url === '/admin/dashboard';
        return url.startsWith(href);
    }

    return (
        <div className="fixed bottom-5 left-1/2 z-[1600] w-[92%] max-w-md -translate-x-1/2 lg:hidden">
            <nav className="relative flex items-center justify-between rounded-2xl border border-white/[0.08] bg-admin-deep/95 px-1.5 py-1.5 shadow-2xl shadow-black/30 backdrop-blur-xl">
                {primaryTabs.map((tab) => {
                    const active = isActive(tab.href);
                    const Icon = tab.icon;

                    return (
                        <Link
                            key={tab.key}
                            href={tab.href}
                            className={cn(
                                'relative flex flex-1 flex-col items-center justify-center rounded-xl py-2.5 transition-all duration-200',
                                active ? 'text-white' : 'text-white/35 active:text-white/60',
                            )}
                        >
                            <AnimatePresence>
                                {active && (
                                    <m.div
                                        layoutId="admin-tab-pill"
                                        className="absolute inset-1 rounded-xl bg-white/[0.08]"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                                    />
                                )}
                            </AnimatePresence>
                            <div className="relative z-10 flex flex-col items-center gap-1">
                                <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.5 : 1.8} />
                                <span className={cn(
                                    'text-[7px] font-bold tracking-wider uppercase',
                                    active ? 'opacity-100' : 'opacity-50',
                                )}>
                                    {tab.name}
                                </span>
                            </div>
                        </Link>
                    );
                })}

                {/* More */}
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                    <SheetTrigger asChild>
                        <button className="relative flex flex-1 flex-col items-center justify-center rounded-xl py-2.5 text-white/35 transition-all active:text-white/60">
                            <div className="relative z-10 flex flex-col items-center gap-1">
                                <MoreHorizontal className="h-[18px] w-[18px]" strokeWidth={1.8} />
                                <span className="text-[7px] font-bold tracking-wider uppercase opacity-50">
                                    Lainnya
                                </span>
                            </div>
                        </button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl border-t border-white/[0.06] bg-admin-deep px-5 pb-10 pt-8 text-white shadow-2xl">
                        <SheetHeader className="mb-8 text-center">
                            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/10" />
                            <SheetTitle className="text-lg font-bold tracking-tight text-white">
                                Menu Lainnya
                            </SheetTitle>
                            <p className="text-[9px] font-medium tracking-[0.15em] text-white/30 uppercase">
                                Navigasi Cepat
                            </p>
                        </SheetHeader>
                        <div className="grid grid-cols-3 gap-2.5">
                            {mainNavItems.map((item) => {
                                if (primaryTabs.some(p => p.href === item.href)) return null;

                                return (
                                    <Link
                                        key={item.title}
                                        href={item.href}
                                        onClick={() => setIsOpen(false)}
                                        className="group flex flex-col items-center gap-2.5 rounded-2xl bg-white/[0.04] p-4 transition-all active:scale-95 hover:bg-white/[0.08]"
                                    >
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-admin-primary/15 text-admin-primary transition-colors group-hover:bg-admin-primary group-hover:text-white">
                                            {item.icon && <item.icon className="h-5 w-5" />}
                                        </div>
                                        <span className="text-center text-[8px] font-bold leading-tight tracking-tight text-white/50 uppercase group-hover:text-white/80">
                                            {item.title}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>

                        <Link
                            href="/dashboard"
                            className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-2xl bg-admin-gold px-6 py-3.5 text-xs font-bold tracking-wide text-admin-deep transition-all active:scale-95 hover:bg-admin-gold/90"
                        >
                            <LogOut className="h-4 w-4" />
                            Keluar Mode Admin
                        </Link>

                        <div className="mt-8 text-center">
                            <div className="flex items-center justify-center gap-2">
                                <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                                <span className="text-[8px] font-medium text-white/25">
                                    v{(usePage().props as any).config.version}
                                </span>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </nav>
        </div>
    );
}
